import { useEffect, useMemo, useState } from "react";
import { api, type Repeat, type Task } from "../api";
import { go, loadTask, upsertTask, useStore } from "../store";
import { dayLabel, dueLabel, isLate, parseTask, REPEAT_LABEL, timeLabel } from "../text";
import { Composer } from "./Composer";
import { MessageItem } from "./MessageItem";
import { Check, Ini, isOwner, roomTitle, setStatus, toast, useMe } from "./ui";

const STATUS = { open: "Open", doing: "In progress", done: "Done" } as const;
const order = (a: Task, b: Task) => {
  const k = (t: Task) => (t.status === "done" ? 9 : isLate(t) ? 0 : t.due ? 1 : 2);
  return k(a) - k(b) || (a.due ?? "9").localeCompare(b.due ?? "9") || a.id - b.id;
};

function Row({ t }: { t: Task }) {
  const users = useStore((s) => s.users);
  const late = isLate(t);
  return (
    <div className={`trw${t.status === "done" ? " done" : ""}`} onClick={() => go({ v: "task", id: t.id })}>
      <Check task={t} />
      <span className="tt">{t.title}</span>
      <span className="w"><Ini user={users[t.ownerId]} />{users[t.ownerId]?.name}</span>
      <span className={`d${late ? " late" : ""}`}>{dueLabel(t.due)}{t.repeat && <span title={`repeats ${REPEAT_LABEL[t.repeat]}`}> ↻</span>}</span>
      <span className="s">{late ? <span className="late">late</span> : STATUS[t.status]}</span>
      <span className="n">{t.replyCount ? `${t.replyCount} 💬` : ""}</span>
    </div>
  );
}
const Header = () => <div className="hd2"><span /><span>Task</span><span>Who</span><span>Due</span><span>Status</span><span /></div>;

export function TasksPage({ tab }: { tab: "everyone" | "mine" }) {
  const me = useMe();
  const tasks = useStore((s) => s.tasks);
  const users = useStore((s) => s.users);
  const [who, setWho] = useState<number | "all">("all");
  const owner = isOwner(me);
  const view = owner ? tab : "mine";
  const all = useMemo(() => Object.values(tasks).sort(order), [tasks]);
  const people = Object.values(users).filter((u) => u.active);

  let body;
  if (view === "mine") {
    const mine = all.filter((t) => t.ownerId === me.id);
    const block = (h: string, l: Task[]) => l.length > 0 && <div key={h}><div className="grh">{h} <span>{l.length}</span></div>{l.map((t) => <Row key={t.id} t={t} />)}</div>;
    body = <>
      <Header />
      {block("Late", mine.filter(isLate))}
      {block("Open", mine.filter((t) => t.status !== "done" && !isLate(t)))}
      {owner && block("Given by you, open", all.filter((t) => t.givenBy === me.id && t.ownerId !== me.id && t.status !== "done"))}
      {block("Done", mine.filter((t) => t.status === "done"))}
      {!mine.length && !owner && <div className="empty" style={{ marginTop: 40 }}>Nothing on you.</div>}
    </>;
  } else {
    const list = all.filter((t) => who === "all" || t.ownerId === who);
    const groups = [...new Set(list.map((t) => t.ownerId))].sort((a, b) => (users[a]?.name ?? "").localeCompare(users[b]?.name ?? ""));
    body = <>
      <Header />
      {groups.map((p) => { const l = list.filter((t) => t.ownerId === p); return (
        <div key={p}><div className="grh">{users[p]?.name} <span>{l.filter((t) => t.status !== "done").length} open</span></div>{l.map((t) => <Row key={t.id} t={t} />)}</div>
      ); })}
      {!list.length && <div className="empty" style={{ marginTop: 40 }}>No tasks yet. Write one below, or press + Task on any message.</div>}
    </>;
  }

  const give = async (text: string) => {
    const p = parseTask(text, people, null);
    if (!p.ownerId) { toast("Start with who it's for, e.g. “Hannah, …”"); return false; }
    if (!p.title) { toast("What's the task?"); return false; }
    try { const t = await api.newTask({ title: p.title, ownerId: p.ownerId, due: p.due, repeat: p.repeat }); upsertTask(t); toast(`Task for ${users[p.ownerId]?.name}`); }
    catch (e: any) { toast(e.message || "Couldn't make the task"); return false; }
  };

  return (
    <div className="col">
      <div className="head">
        <b>Tasks</b>
        {owner ? (
          <span className="seg">
            <button className={view === "everyone" ? "on" : ""} onClick={() => go({ v: "tasks", tab: "everyone" })}>Everyone</button>
            <button className={view === "mine" ? "on" : ""} onClick={() => go({ v: "tasks", tab: "mine" })}>Mine</button>
          </span>
        ) : <span>· yours</span>}
      </div>
      {owner && view === "everyone" && (
        <div className="flt">
          <button className={who === "all" ? "on" : ""} onClick={() => setWho("all")}>Everyone</button>
          {people.map((u) => <button key={u.id} className={who === u.id ? "on" : ""} onClick={() => setWho(u.id)}>{u.name}</button>)}
        </div>
      )}
      <div className="thread rows">{body}</div>
      {owner && (
        <Composer draftKey="new-task" files={false} placeholder="New task: “Hannah, check the Brookside report by Friday”"
          preview={(text) => { const p = parseTask(text, people, null); return p.ownerId
            ? <><span>Task for <b>{users[p.ownerId]?.name}</b></span>{p.due && <span>· due <b>{dueLabel(p.due)}</b></span>}{p.repeat && <span>· ↻ {REPEAT_LABEL[p.repeat]}</span>}<span>· Enter to give</span></>
            : <span>Start with who it's for: {people.map((u) => u.name).join(", ")}</span>; }}
          onSend={(body) => give(body)} />
      )}
    </div>
  );
}

export function TaskPage({ id }: { id: number }) {
  const me = useMe();
  const task = useStore((s) => s.tasks[id]);
  const events = useStore((s) => s.events[id]);
  const source = useStore((s) => s.sources[id]);
  const users = useStore((s) => s.users);
  const rooms = useStore((s) => s.rooms);
  const [missing, setMissing] = useState(false);
  const [editTitle, setEditTitle] = useState<string | null>(null);
  const owner = isOwner(me);

  useEffect(() => { setMissing(false); loadTask(id).catch(() => setMissing(true)); }, [id]);

  if (!task) return <div className="col"><div className="head"><button className="back" onClick={() => history.back()}>‹ Back</button></div><div className="empty" style={{ marginTop: 60 }}>{missing ? "This task isn't there." : "Loading…"}</div></div>;

  const edit = async (p: Parameters<typeof api.editTask>[1]) => {
    try { upsertTask(await api.editTask(id, p)); } catch (e: any) { toast(e.message || "Couldn't change it"); }
  };
  const room = task.roomId ? rooms[task.roomId] : undefined;
  const late = isLate(task);
  const ownerName = users[task.ownerId]?.name ?? "";
  const people = Object.values(users).filter((u) => u.active);

  const sysLine = (e: NonNullable<typeof events>[number]) => {
    const who = e.userId ? users[e.userId]?.name ?? "Someone" : "";
    const at = `${dayLabel(e.createdAt) === "Today" ? "" : dayLabel(e.createdAt) + " "}${timeLabel(e.createdAt)}`;
    const d = e.data ?? {};
    if (e.kind === "created") return d.repeatOf ? `Came back (repeats) · ${at}` : `${who} ${d.fromMessage ? "made this from a message" : "gave this"} · ${at}`;
    if (e.kind === "status") return `${who}: ${STATUS[d.to as Task["status"]]?.toLowerCase()} · ${at}`;
    if (e.kind === "next") return `Next one due ${dueLabel(d.due as string)} · ${at}`;
    if (e.kind === "edit") {
      const parts = [d.title && "renamed it", "due" in d && (d.due ? `set due ${dueLabel(d.due as string)}` : "removed the due date"),
        "repeat" in d && (d.repeat ? `set it to repeat ${REPEAT_LABEL[d.repeat as Repeat]}` : "stopped it repeating"),
        d.owner_id && `gave it to ${users[d.owner_id as number]?.name}`].filter(Boolean);
      return `${who} ${parts.join(", ")} · ${at}`;
    }
    return "";
  };

  return (
    <div className="col">
      <div className="head">
        <button className="back" onClick={() => (history.length > 1 ? history.back() : go({ v: "tasks", tab: owner ? "everyone" : "mine" }))}>‹ Back</button>
        <span>· task{room ? ` from ${roomTitle(room, me.id, users)}` : ""}</span>
      </div>
      <div className="thread" style={{ paddingTop: 22 }}>
        <div className="tk">
          <div className="ti">
            <Check task={task} size={20} />
            {editTitle !== null
              ? <input autoFocus value={editTitle} onChange={(e) => setEditTitle(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { edit({ title: editTitle }); setEditTitle(null); } if (e.key === "Escape") setEditTitle(null); }}
                  onBlur={() => { if (editTitle.trim() && editTitle !== task.title) edit({ title: editTitle }); setEditTitle(null); }} />
              : <span>{task.title}{owner && <button className="edit" onClick={() => setEditTitle(task.title)}>edit</button>}</span>}
          </div>
          <div className="mt">
            {owner ? (
              <>
                <select value={task.ownerId} onChange={(e) => edit({ ownerId: Number(e.target.value) })}>{people.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}</select>
                · due <input type="date" value={task.due ?? ""} onChange={(e) => edit({ due: e.target.value || null })} />
                {late && <span className="late">late</span>}
                · <select value={task.repeat ?? ""} onChange={(e) => edit({ repeat: e.target.value || null })}>
                  <option value="">doesn't repeat</option><option value="daily">↻ every day</option><option value="weekdays">↻ every weekday</option><option value="weekly">↻ every week</option><option value="monthly">↻ every month</option>
                </select>
                {task.via === "neo" && <span>· via Neo</span>}
              </>
            ) : (
              <>
                {ownerName}{task.due && <> · <span className={late ? "late" : ""}>due {dueLabel(task.due)}{late ? ", late" : ""}</span></>}
                {" "}· given by {users[task.givenBy]?.name}{task.repeat && ` · ↻ repeats ${REPEAT_LABEL[task.repeat]}`}
              </>
            )}
          </div>
          {(owner || task.ownerId === me.id) && (
            <div className="sts">{(["open", "doing", "done"] as const).map((k) => <button key={k} className={task.status === k ? "on" : ""} onClick={() => setStatus(task, k)}>{STATUS[k]}</button>)}</div>
          )}
        </div>
        {source && room && (
          <div className="quote" onClick={() => go({ v: "room", id: source.roomId, focus: source.id })}>
            <small>From {roomTitle(room, me.id, users)} · {users[source.userId]?.name} · {timeLabel(source.createdAt)}</small>
            {source.body}
          </div>
        )}
        {(events ?? []).map((e) => e.kind === "reply"
          ? <MessageItem key={e.id} inThread bare cont={false} item={{ kind: "msg", m: { id: -e.id, roomId: 0, userId: e.userId!, body: e.body ?? "", parentId: null, alsoInChat: false, clientId: null, replyCount: 0, lastReplyAt: null, createdAt: e.createdAt, editedAt: null, deletedAt: null, files: e.files, reactions: [], mentions: [], preview: null } }} />
          : <div key={e.id} className="sys">{sysLine(e)}</div>)}
        {task.prevTaskId && <div className="sys"><button className="back" onClick={() => go({ v: "task", id: task.prevTaskId! })}>See the last time</button></div>}
      </div>
      <Composer draftKey={`task-${id}`} placeholder={task.ownerId === me.id ? "Add a note" : `Reply about this task · ${ownerName} is told`}
        onSend={async (body, files) => {
          try { await api.replyTask(id, body, files.map((f) => f.id)); loadTask(id).catch(() => {}); }
          catch (e: any) { toast(e.message || "Not sent"); return false; }
        }} />
    </div>
  );
}

